/**
 * BuyerParent (Accounts Receivable) Screen
 *
 * Point-in-time AR snapshot grouped by buyer parent. For a user-selected
 * reference date, shows each buyer's total debit/credit split by aging
 * bucket (<90 vs >90 days), plus eligibility exclusions (intercompany,
 * government, non-corp, jurisdiction, extended terms, credit notes).
 * "AR Before/After X-Aging" shows the effect of cross-aging adjustments.
 *
 * Data flow:
 *  - Unlike time-series screens, this requires a reference date input.
 *  - `dateRange` is fetched from the backend to constrain the date picker
 *    to the range of dates available in the filtered master frame.
 *  - `dateChanged` flag hides stale results when the user picks a new date
 *    but has not yet recalculated.
 *  - CalcParamsModal exposes AR-specific thresholds (e.g., cross-aging %).
 */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateBuyerParent, getBuyerParentData, getDateRange } from "Redux/data/actions";
import { useResult, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import CalcParamsModal, { ParamsButton } from "Components/CalcParamsModal";
import { BUYER_PARENT_FIELDS } from "Config/calcParamsConfig";

const headers = [
  { text: "Buyer Parent Name", key: "Buyer_Parent_Name", datatype: "string" },
  { text: "Total Debit", key: "total_debit", datatype: "float" },
  { text: "Total Credit", key: "total_credit", datatype: "float" },
  { text: "Debit (<90 Days)", key: "total_debit_under_90", datatype: "float" },
  { text: "Credit (<90 Days)", key: "total_credit_under_90", datatype: "float" },
  { text: "Debit (>90 Days)", key: "total_debit_over_90", datatype: "float" },
  { text: "Credit (>90 Days)", key: "total_credit_over_90", datatype: "float" },
  { text: "Default Receivables", key: "default_receivables", datatype: "float" },
  { text: "Intercompany", key: "intercompany", datatype: "float" },
  { text: "Government", key: "government", datatype: "float" },
  { text: "Non-Corp", key: "non_corp", datatype: "float" },
  { text: "Jurisdiction", key: "jurisdiction", datatype: "float" },
  { text: "Extended Terms", key: "extended_terms", datatype: "float" },
  { text: "Credit Notes", key: "credit_notes", datatype: "float" },
  { text: "AR Before X-Aging", key: "ar_before_xaging", datatype: "float" },
  { text: "AR After X-Aging", key: "ar_after_xaging", datatype: "float" },
];


const DEFAULT_DATE = "2022-12-27";

const BuyerParent = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);
  const [referenceDate, setReferenceDate] = useState(DEFAULT_DATE);
  const [dateChanged, setDateChanged] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const buyerParent = useResult("buyerParent");
  const dateRange = useSelector((state) => state.data.dateRange);
  const hasMaster = useScenarioFlag("hasMaster");
  const hasBuyerParent = useScenarioFlag("hasBuyerParent");

  useEffect(() => {
    if (hasMaster && !dateRange) {
      dispatch(getDateRange());
    }
  }, [hasMaster, dateRange, dispatch]);

  useEffect(() => {
    if (hasBuyerParent && !buyerParent && !dateChanged) {
      dispatch(getBuyerParentData(() => {}));
    }
  }, [hasBuyerParent, buyerParent, dateChanged, dispatch]);

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  const showData = !dateChanged && buyerParent;
  const buttonText = hasBuyerParent && !dateChanged ? "Recalculate" : "Calculate";

  return (
    <div>
      {showParams && (
        <CalcParamsModal
          section="buyerParent"
          title="Accounts Receivable Parameters"
          fields={BUYER_PARENT_FIELDS}
          onClose={() => setShowParams(false)}
        />
      )}
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <label className="label" style={{ margin: 0, whiteSpace: "nowrap" }}>Reference Date</label>
          <input
            className="input is-small"
            type="date"
            value={referenceDate}
            min={dateRange ? dateRange.min_date : undefined}
            max={dateRange ? dateRange.max_date : undefined}
            onChange={(e) => {
              setReferenceDate(e.target.value);
              setDateChanged(true);
            }}
            style={{ width: "180px" }}
          />
        </div>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(
              calculateBuyerParent(referenceDate, () => {
                setCalculating(false);
                setDateChanged(false);
              })
            );
          }}
        >
          {buttonText} Accounts Receivable
        </button>
        <ParamsButton onClick={() => setShowParams(true)} />
      </div>
      {showData && (
        <DataTable
          data={buyerParent.data}
          headers={headers}
          reference="Buyer_Parent_Name"
        />
      )}
    </div>
  );
};

export default BuyerParent;
